import { useState } from "react";

/**
 * Image element with a graceful fallback when the source fails to load.
 *
 * Three distinct fallback states:
 *   • noSrc + name provided — render a deterministic pastel gradient with the
 *     first 1-2 letters of `name` as a clean monogram. Used by product cards,
 *     cart line items, order summaries — anything where the AI image hasn't
 *     been generated yet but we still want a polished, recognisable tile.
 *   • noSrc + no name — render an animated shimmer (background generators
 *     in progress). Reads as "loading" rather than "broken".
 *   • errored — `src` was set but the browser failed to load it. Show a soft
 *     warm "Image unavailable" tile so the merchant notices.
 */

// Two-letter monogram from a product / business name.
const initialsOf = (name) => {
  if (!name || typeof name !== "string") return "";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Deterministic pastel gradient picked from `name` so a given product always
// renders the same tile. Two stops, soft hues, never muddy on dark text.
const PALETTES = [
  ["#FFE8D6", "#FFB5A7"], // peach
  ["#FFE2EC", "#FFC2D4"], // pink
  ["#E0EAFC", "#CFDEF3"], // sky
  ["#D4F1F4", "#B6E3DA"], // mint
  ["#FFF1C7", "#FFD874"], // butter
  ["#EBE4FF", "#C7B9FF"], // lavender
  ["#FFE5D1", "#FCB9A9"], // salmon
  ["#E1F4E5", "#A8D8B9"], // sage
];

const gradientFor = (name) => {
  let h = 0;
  const s = name || "•";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length];
};

export default function SafeImage({
  src,
  alt = "",
  name,
  className = "",
  style = {},
  fallbackLabel = "Image unavailable",
  fallbackStyle,
  testId,
}) {
  const [errored, setErrored] = useState(false);
  const noSrc = !src || typeof src !== "string" || src.trim().length === 0;

  // ── Name-driven monogram tile ────────────────────────────────────────────
  if (noSrc && name) {
    const initials = initialsOf(name);
    const [a, b] = gradientFor(name);
    return (
      <div
        className={`flex items-center justify-center select-none ${className}`}
        style={{
          background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
          color: "rgba(15,23,42,0.45)",
          minHeight: 120,
          ...(fallbackStyle || {}),
          ...style,
        }}
        role="img"
        aria-label={alt || name}
        data-testid={testId ? `${testId}-initials` : undefined}
      >
        <span
          style={{
            fontSize: "min(48%, 56px)",
            fontWeight: 800,
            letterSpacing: "0.04em",
            fontFamily: "'Montserrat', system-ui, sans-serif",
            lineHeight: 1,
          }}
        >
          {initials || "·"}
        </span>
      </div>
    );
  }

  // ── Shimmer (no src, no name) ─────────────────────────────────────────────
  if (noSrc) {
    return (
      <div
        className={`safe-image-skeleton flex items-center justify-center text-[10px] font-semibold uppercase tracking-wider ${className}`}
        style={{
          background: "linear-gradient(135deg, #F1F5F9 0%, #E8F5EE 100%)",
          color: "#94A3B8",
          minHeight: 120,
          backgroundSize: "200% 100%",
          animation: "safeImageShimmer 2.4s linear infinite",
          ...(fallbackStyle || {}),
          ...style,
        }}
        role="img"
        aria-label={alt || "Image loading"}
        data-testid={testId ? `${testId}-loading` : undefined}
      >
        {fallbackLabel === "Image unavailable" ? "" : fallbackLabel}
      </div>
    );
  }
  if (errored) {
    return (
      <div
        className={`flex items-center justify-center text-xs font-semibold uppercase tracking-wider ${className}`}
        style={{
          background: "linear-gradient(135deg, #FEE2E2 0%, #FEF3C7 100%)",
          color: "#B45309",
          minHeight: 120,
          ...(fallbackStyle || {}),
          ...style,
        }}
        role="img"
        aria-label={alt || fallbackLabel}
        data-testid={testId ? `${testId}-fallback` : undefined}
      >
        {fallbackLabel}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => setErrored(true)}
      loading="lazy"
      data-testid={testId}
    />
  );
}
